import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  parseLanguageFromPath,
  supportedLanguages,
  SupportedLanguage,
} from "../utils/languageRoutes";

interface LanguageRouterProps {
  children: React.ReactNode;
}

export const LanguageRouter: React.FC<LanguageRouterProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const { language } = parseLanguageFromPath(location.pathname);

    // If we're at root path without language prefix
    if (location.pathname === "/") {
      // Detect browser language or use saved preference
      const browserLang = navigator.language.split("-")[0];
      const savedLang = localStorage.getItem("i18nextLng");
      const detectedLang =
        savedLang && supportedLanguages.includes(savedLang as SupportedLanguage)
          ? (savedLang as SupportedLanguage)
          : supportedLanguages.includes(browserLang as SupportedLanguage)
          ? (browserLang as SupportedLanguage)
          : "en";

      navigate(`/${detectedLang}`, { replace: true });
      return;
    }

    // If URL doesn't have language prefix, add it
    if (
      !supportedLanguages.includes(
        location.pathname.split("/")[1] as SupportedLanguage
      )
    ) {
      const currentLang = (i18n.language || "en") as SupportedLanguage;
      navigate(`/${currentLang}${location.pathname}`, { replace: true });
      return;
    }

    // Update i18n language if it differs from URL
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [location.pathname, navigate, i18n]);

  return <>{children}</>;
};
